<template>
  <div class="market-info bg--color-02">
    <div class="market-info__container container-new pt-32 pb-32">
      <market-info-header
        v-model="search"
        :placeholder="$t('marketInfo.search')"
        :title="$t('marketInfo.title')"
      />
      <market-info-table
        :show-load-more="showLoadMore"
        :columns="tableColumns"
        :loading="loading"
        :rows="currentVisiblePairs"
        @show-more="showMore"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import Decimal from 'decimal.js-light'
import defaultHeadMeta from '@/mixins/defaultHeadMeta'

const MARKET_VOLUME = new Map()
const VOLUME_CALC_TIMEOUT = 30 * 1000 // 30 sec
const MARKET_CAP_UPDATE_TIMEOUT = 60 * 1000 * 10 // 10 min
const LIMIT = 6

export default {
  name: 'MarketInfo',

  components: {
    MarketInfoHeader: () =>
      import(
        /* webpackMode: "eager" */ '@/components/market-info/MarketInfoHeader'
      ),
    MarketInfoTable: () =>
      import(
        /* webpackMode: "eager" */ '@/components/tables/market-info/MarketInfoTable'
      ),
  },

  mixins: [defaultHeadMeta],

  data () {
    return {
      marketCapUpdate: null,
      selectedMarket: 'SPOT',
      loading: true,
      search: '',
      page: 1,
    }
  },

  async fetch () {
    await Promise.allSettled([this.getMarketCap(), this.getFrontCurrencies()])
  },

  computed: {
    ...mapGetters({
      // getLatest: 'welcome/getLatestCoins',
      getMarketPairs: 'pairs/marketPairs',
      marketTabs: 'pairs/marketTabs',
      marketPairs: 'pairs/getCoinGroups',
      marketCap: 'welcome/getMarketCap',
      // listing: 'welcome/getListing',
      pairsList: 'pairs/pairsList',
    }),

    tabs () {
      return this.tabsContent.map(({ id, text, withLabel }) => ({
        id,
        text,
        withLabel,
      }))
    },

    tabsContent () {
      return [...this.marketTabs].map(this.createTabs)
    },

    currentLimit () {
      return this.page * LIMIT
    },

    showLoadMore () {
      if (process.server) return false

      const { length } = this.search
        ? this.currentSearchedPairs
        : this.currentMarketPairs

      return !this.loading && length > this.currentLimit
    },

    tableColumns () {
      return ['coin', 'price', 'change', 'volume', 'market', 'action']
    },

    currentMarketPairs () {
      if (process.server) return []

      const current = this.tabsContent.find(
        ({ id }) => id === this.selectedMarket,
      )

      if (!current) return []

      const extractedPairs = this.extractPairsFromTab(current)

      return Object.entries(
        extractedPairs.reduce(this.createPairGroup, {}),
      ).filter(([key]) => `${key}_USDT` in this.pairsList)
    },

    currentList () {
      return (
        (this.search ? this.currentSearchedPairs : this.currentMarketPairs) ||
        []
      )
    },

    currentVisiblePairs () {
      // coins with cupRank
      const sortedByMarketCapRank = [...this.currentList]
        .filter(el => this.marketCap[el[0]]?.cupRank)
        .sort(this.sortPairsByMarketCapRank)

      // coins without cupRank
      const otherPairs = [...this.currentList]
        .filter(el => !this.marketCap[el[0]]?.cupRank)
        .sort(this.sortPairsByVolume)

      const result = sortedByMarketCapRank.concat(otherPairs)

      if (this.showLoadMore) {
        return result.slice(0, this.currentLimit)
      }

      return result
    },

    currentSearchedPairs () {
      if (!this.search || process.server) return []

      return this.currentMarketPairs.filter(this.searchFilter)
    },
  },

  async mounted () {
    if (process.client) {
      this.loading = true
      await Promise.allSettled([this.$api.$marketSubscribe()])
        .then(() => {
          this.marketCapUpdate = setInterval(
            () => this.getMarketCap(),
            MARKET_CAP_UPDATE_TIMEOUT,
          )
        })
        .finally(() => {
          this.loading = false
        })
    }
  },

  beforeDestroy () {
    if (process.client) {
      clearInterval(this.marketCapUpdate)
      this.$api.$marketUnsubscribe()
    }
  },

  methods: {
    ...mapActions({
      getMarketCap: 'welcome/getMarketCap',
      getFrontCurrencies: 'welcome/getFrontCurrencies',
    }),

    showMore () {
      this.page += 1
    },

    createTabs (tab) {
      return {
        ...tab,
        text: this.$t(`marketInfo.tabs.${tab.id.toLowerCase()}`),
      }
    },

    checkIsUSDT ({ moneyTicker }) {
      return moneyTicker === 'USDT'
    },

    searchFilter (data) {
      const inPair = data[0].toLowerCase()
      const inSearch = this.search.toLowerCase()

      return inPair.includes(inSearch)
    },

    extractPairsFromTab ({ tabs, pairs }) {
      if (tabs && tabs.length > 0) {
        return tabs.map(this.extractPairsFromTab).flat()
      }

      return pairs
    },

    createPairGroup (all, curr) {
      return {
        ...all,
        [curr.stockTicker]: [...(all[curr.stockTicker] || []), curr],
      }
    },

    sortPairsByMarketCapRank ([a], [b]) {
      const rankA = this.marketCap[a]?.cupRank || 1000000
      const rankB = this.marketCap[b]?.cupRank || 1000000

      return rankA - rankB
    },

    sortPairsByVolume (a, b) {
      const [keyA, valueA] = a
      const [keyB, valueB] = b
      const volumeA =
        this.marketCap[keyA]?.totalVolume ||
        this._memoVolumeResolver(keyA, valueA)
      const volumeB =
        this.marketCap[keyB]?.totalVolume ||
        this._memoVolumeResolver(keyB, valueB)

      return +volumeB - +volumeA
    },

    getUSDTPrice ({ volume, moneyTicker }) {
      let price = new Decimal(0)

      if (!this.checkMoneyValue(volume)) return price

      const currentTicker = this.marketPairs[moneyTicker]?.find(
        this.checkIsUSDT,
      )

      if (this.checkMoneyValue(currentTicker?.price)) {
        const volumeDecimal = new Decimal(volume)
        const priceDecimal = new Decimal(currentTicker.price)

        price = volumeDecimal.mul(priceDecimal)
      }

      return price
    },

    checkMoneyValue (str) {
      if (!str) return false

      const num = parseFloat(`${str}`)

      return !isNaN(num) && !!num
    },

    calcTotalVolumeInUSDT (pairsGroup) {
      return pairsGroup
        .reduce(
          (total, current) =>
            total.plus(
              this.checkIsUSDT(current)
                ? new Decimal(current.volumeInMoney)
                : this.getUSDTPrice(current),
            ),
          new Decimal(0),
        )
        .toString()
    },

    _memoVolumeResolver (ticker, pairsGroup) {
      const date = Date.now()
      const key = JSON.stringify(pairsGroup.map(({ id }) => id)) + ticker

      if (MARKET_VOLUME.has(key)) {
        const fromMemo = MARKET_VOLUME.get(key)

        if (date - fromMemo.date < VOLUME_CALC_TIMEOUT) {
          return fromMemo.result
        } else {
          MARKET_VOLUME.delete(key)
        }
      }

      const volume =
        this.marketCap[ticker]?.totalVolume ||
        this.calcTotalVolumeInUSDT(pairsGroup)
      const result = volume || ''

      MARKET_VOLUME.set(key, { date, result })

      return result
    },
  },
}
</script>

<style lang="scss" scoped>
.market-info {
  flex-grow: 1;

  &__container {
    display: flex;
    flex-direction: column;
    row-gap: toRem(24px);

    @include bp(l) {
      row-gap: toRem(28px);
    }
  }
}
</style>
